/* eslint-disable @typescript-eslint/no-explicit-any */
import { serverTimestamp } from 'firebase/firestore';
import { i18n } from 'i18next';

import {
  CreditImportance,
  CreditImportanceOptionsEnum,
  CreditScoreImportance,
  CreditScoreImportanceOptionsEnum,
  DevLoanTypes,
  EngagementAttributes,
  EngagementData,
  EngagementInfo,
  EngagementProfile,
  FutureLoanPlans,
  LoanOptions,
  LoanOptionsEnum,
  STATUS_OPTIONS,
  LeadType,
} from '@types';
import { store } from 'src/stores';

import { getFbclid, getTrustedFormUrl } from './common';
import { getPercentComplete } from './module';

const handleNullishCurrencyValue = (value: string | number | null) => {
  return value === null ? null : Number(value);
};

export const formatLoanIn2Years = (
  futureLoanPlans: FutureLoanPlans,
  loanType: DevLoanTypes
): LoanOptions => {
  const loanValue =
    loanType === DevLoanTypes.MORTGAGE
      ? futureLoanPlans.mortgage_in_2_years
      : futureLoanPlans.auto_in_2_years;

  if (loanValue === true) {
    return LoanOptionsEnum.YES;
  } else if (loanValue === false) {
    return LoanOptionsEnum.NO;
  } else {
    return LoanOptionsEnum.EMPTY;
  }
};

export const formatCreditImportance = (
  creditScoreImportance: CreditScoreImportance
): CreditImportance => {
  if (creditScoreImportance === CreditScoreImportanceOptionsEnum.NO) {
    return CreditImportanceOptionsEnum.IMPT;
  } else if (
    creditScoreImportance == CreditScoreImportanceOptionsEnum.YES ||
    creditScoreImportance == CreditScoreImportanceOptionsEnum.MAYBE
  ) {
    return CreditImportanceOptionsEnum.NOT_IMPT;
  } else {
    return CreditImportanceOptionsEnum.EMPTY;
  }
};

export const compileEngagementAttributes = (
  data: any
): EngagementAttributes => {
  const {
    creditScore,
    creditScoreImportance,
    debtTypes,
    debtCause,
    debtAmount,
    employmentStatus,
    futureLoanPlans,
    debtGoal,
    householdIncome,
    incomeConsistency,
    propertyType,
    paymentStatus,
  } = data;

  return {
    credit_rating: creditScore ?? '',
    credit_importance: formatCreditImportance(creditScoreImportance) ?? '',
    debt_types: debtTypes ?? '',
    debt_amount: handleNullishCurrencyValue(debtAmount) ?? '',
    hardship_reason: debtCause ?? '',
    employment_status: employmentStatus ?? '',
    mortgage_in_2_years: futureLoanPlans
      ? formatLoanIn2Years(futureLoanPlans, DevLoanTypes.MORTGAGE)
      : '',
    auto_in_2_years: futureLoanPlans
      ? formatLoanIn2Years(futureLoanPlans, DevLoanTypes.AUTO)
      : '',
    relief_goal: debtGoal ?? '',
    household_income: handleNullishCurrencyValue(householdIncome) ?? '',
    hhi_consistency: incomeConsistency ?? '',
    homeowner_status: propertyType ?? '',
    payment_status: paymentStatus ?? '',
  };
};

export const compileEngagementProfile = (
  data: any,
  language: string
): EngagementProfile => {
  const { firstName, lastName, email, phone, state, zipCode } = data;

  const { enableZipCodeField } = store.featureFlagStore.featureFlags;
  const address: { state?: string; zip?: string } = {};

  if (enableZipCodeField) {
    address.state = state;
    address.zip = zipCode;
  } else {
    address.state = state;
  }

  return {
    address,
    contact_preference: '',
    email: email,
    first_name: firstName,
    language: language,
    last_name: lastName,
    phone_mobile: phone,
  };
};

export const compileBaselineEngagementData = (
  status: string,
  variant: string,
  engagementInfo: EngagementInfo,
  data: any,
  leadType: LeadType
): EngagementData => {
  const {
    user,
    ip,
    gaClientId,
    partner,
    campaignCallNumber,
    pid,
    i18n,
    percentComplete,
    pageTitle,
    hostUrl,
  } = engagementInfo;

  let res = {
    attributes: compileEngagementAttributes(data),
    updated_by: user.uid,
    created_by: user.uid,
    date_updated: serverTimestamp(),
    experiment_id: '',
    experiment_name: variant,
    id: user.uid,
    ga_client_id: store.initializeStore.gaClientId || gaClientId || '',
    partner_id: partner.id!,
    partner_name: partner.displayname!,
    profile: compileEngagementProfile(data, i18n.language),
    remote_ip: ip ?? '',
    status: status,
    campaign_call_number: campaignCallNumber ?? '',
    PID: pid ?? '',
    lead_type: leadType ?? '',
    partnerReferenceId: partner.reference_id!,
    host_url: hostUrl ?? '',
    last_page_collected: pageTitle ?? 'No module title found',
    fbclid: getFbclid(store.initializeStore.options.requestingHostLong),
  } as EngagementData;

  const trustedFormUrl = getTrustedFormUrl();

  if (trustedFormUrl) {
    res = {
      ...res,
      applicant_trusted_form_url: trustedFormUrl,
    };
  }

  if (percentComplete) {
    res = {
      ...res,
      percent_complete: percentComplete,
    };
  }

  return res as EngagementData;
};

export const handleEngagementStatus = (nextModuleName: string) => {
  switch (nextModuleName) {
    case 'MODULE_REVIEW':
      return STATUS_OPTIONS.READY;
    default:
      return STATUS_OPTIONS.PENDING;
  }
};

export const compileEngagementInfo = (i18n: i18n): EngagementInfo => {
  const { user, ip } = store.userStore;
  const { partner } = store.partnerStore;
  const { campaignCallNumber, pid, hostUrl } = store.initializeStore.options;
  const { formConfig, currentModuleName, currentModuleTitle } = store.formStore;
  const percentComplete = getPercentComplete(formConfig, currentModuleName);
  let result = {
    ip,
    campaignCallNumber: campaignCallNumber ?? '',
    pid: pid ?? '',
    i18n: i18n!,
    pageTitle: currentModuleTitle,
    hostUrl: hostUrl ?? '',
  } as EngagementInfo;

  if (user) {
    result = {
      ...result,
      user,
    };
  }
  if (percentComplete) {
    result = {
      ...result,
      percentComplete,
    };
  }
  if (partner) {
    result = {
      ...result,
      partner,
    };
  }

  return result as EngagementInfo;
};
